import { breakpoints } from "@repo/fika-design-system/breakpoints"
import type { AccentHue } from "@repo/fika-design-system/color"
import type { IconName } from "@repo/fika-design-system/icon"
import * as Sentry from "@sentry/solid"
import { createMediaQuery } from "@solid-primitives/media"
import { makeTimer } from "@solid-primitives/timer"
import { A } from "@solidjs/router"
import { assignInlineVars } from "@vanilla-extract/dynamic"
import type { JSXElement } from "solid-js"
import {
  createEffect,
  createMemo,
  createSignal,
  For,
  Match,
  Show,
  Switch,
} from "solid-js"
import { useI18nContext } from "@/i18n/i18n-solid"
import { useVirtualKeyboard } from "@/lib/use-virtual-keyboard"
import { Button } from "../components/button"
import { Icon } from "../components/icon"
import { Spinner } from "../components/spinner"
import { Tooltip } from "../components/tooltip"
import { Info } from "../patterns/info"
import {
  drawerClass,
  drawerCollapseClass,
  drawerCollapseInnerClass,
  errorContainerClass,
  footerClass,
  headerClass,
  headerFocusModeClass,
  itemLinkClass,
  itemLinkCountClass,
  layoutClass,
  loadingStateClass,
  mainClass,
  mainWrapperClass,
  rightPanelClass,
  rightPanelContentClass,
  sidebarContainerClass,
  visualViewportHeightVar,
} from "./main-layout.css"

export type DrawerItem = {
  iconName: IconName
  label: string
  description: string
  href: string
  count?: number
  disabled?:
    | boolean
    | {
        reason?: string
      }
}

type MainLayoutPropsBase = {
  header: JSXElement
  rightPanel?: JSXElement
  drawer?: DrawerItem[]
  footer?: JSXElement
  footerPadded?: boolean
  hue: AccentHue
  focusMode?: boolean
  focusModeButton?: JSXElement
}

type MainLayoutPropsWithData<T> = MainLayoutPropsBase & {
  query: {
    data?: T
    fetching: boolean
    error?: { message: string }
  }
  children: JSXElement | ((data: T) => JSXElement)
}

type MainLayoutPropsWithoutData = MainLayoutPropsBase & {
  query?: never
  children: JSXElement
}

export type MainLayoutProps<T = unknown> =
  | MainLayoutPropsWithData<T>
  | MainLayoutPropsWithoutData

export function MainLayout<T = unknown>(props: MainLayoutProps<T>) {
  const isMobile = createMediaQuery(breakpoints.l)
  const { viewportHeight } = useVirtualKeyboard()
  const { LL } = useI18nContext()
  const [drawerInteractive, setDrawerInteractive] = createSignal(true)

  createEffect(() => {
    if (props.focusMode) {
      setDrawerInteractive(false)
    } else {
      makeTimer(() => setDrawerInteractive(true), 200, setTimeout)
    }
  })

  createEffect(() => {
    const error = props.query?.error
    if (error) {
      Sentry.captureException(new Error(`GraphQL Error: ${error.message}`), {
        extra: {
          errorMessage: error.message,
          context: "MainLayout data fetching",
        },
      })
    }
  })

  function renderContent() {
    if (!props.query) {
      return props.children
    }

    return (
      <Switch>
        <Match when={props.query.fetching}>
          <div class={loadingStateClass}>
            <Spinner />
          </div>
        </Match>
        <Match when={props.query.error}>
          <div class={loadingStateClass}>
            <div class={errorContainerClass}>
              <Info iconName="alert-triangle">
                {LL().mainLayout.errorLoadingData()}
              </Info>
              <Button hue={props.hue} onClick={() => window.location.reload()}>
                {LL().mainLayout.reloadPage()}
              </Button>
            </div>
          </div>
        </Match>
        <Match when={props.query.data !== undefined && props.query.data}>
          {data => {
            if (typeof props.children === "function") {
              return props.children(data())
            }
            return props.children
          }}
        </Match>
      </Switch>
    )
  }

  const layoutStyle = createMemo(() => {
    const h = viewportHeight()
    if (!h) return undefined
    return assignInlineVars({ [visualViewportHeightVar]: `${h}px` })
  })

  return (
    <>
      <div class={layoutClass} style={layoutStyle()}>
        <Show when={props.header}>
          {header => (
            <header
              class={`${headerClass}${props.focusMode ? ` ${headerFocusModeClass}` : ""}`}
            >
              {header()}
            </header>
          )}
        </Show>
        <div class={mainWrapperClass} data-main-scroll>
          <main class={mainClass}>{renderContent()}</main>
        </div>
        <Show
          when={
            !props.focusMode &&
            (props.footer || (!props.rightPanel && isMobile() && props.drawer))
          }
        >
          <footer
            class={footerClass({
              padded:
                (!props.rightPanel && isMobile() && !!props.drawer) ||
                props.footerPadded,
            })}
          >
            <Show when={props.footer}>{footer => footer()}</Show>
            <Show when={!props.rightPanel && isMobile() && props.drawer}>
              {drawer => <Drawer items={drawer()} hue={props.hue} />}
            </Show>
          </footer>
        </Show>
        <Show when={props.rightPanel}>
          {rightPanel => (
            <aside class={rightPanelClass}>
              <div class={rightPanelContentClass}>{rightPanel()}</div>
            </aside>
          )}
        </Show>
      </div>
      <Show
        when={
          !props.rightPanel &&
          !isMobile() &&
          (props.drawer || props.focusModeButton)
        }
      >
        <div class={sidebarContainerClass}>
          <Show when={props.drawer}>
            {drawer => (
              <div
                class={drawerCollapseClass({
                  collapsed: !!props.focusMode,
                  interactive: drawerInteractive() ? undefined : false,
                })}
              >
                <div class={drawerCollapseInnerClass}>
                  <Drawer items={drawer()} hue={props.hue} />
                </div>
              </div>
            )}
          </Show>
          <Show when={props.focusModeButton}>{button => button()}</Show>
        </div>
      </Show>
    </>
  )
}

function Drawer(props: { items: DrawerItem[]; hue: AccentHue }) {
  const isMobile = createMediaQuery(breakpoints.l)

  return (
    <ul class={drawerClass({ mobile: isMobile() })}>
      <For each={props.items}>
        {item => <DrawerItem {...item} hue={props.hue} />}
      </For>
    </ul>
  )
}

function DrawerItem(props: DrawerItem & { hue: AccentHue }) {
  const isMobile = createMediaQuery(breakpoints.l)

  const disabled = createMemo(() => Boolean(props.disabled))

  const disabledReason = createMemo(() => {
    if (!props.disabled || typeof props.disabled === "boolean") return undefined
    return props.disabled.reason
  })

  const tooltipTitle = createMemo(() => {
    if (disabledReason()) {
      return `${props.label} (${disabledReason()})`
    }
    return props.label
  })

  const tooltipDescription = createMemo(() => {
    const reason = disabledReason()
    if (reason) {
      return reason
    }
    return props.description
  })

  return (
    <li>
      <Show
        when={isMobile()}
        fallback={
          <Tooltip
            title={tooltipTitle()}
            description={tooltipDescription()}
            dark
            placement="left"
          >
            <A
              href={props.href}
              class={itemLinkClass({
                mobile: false,
                disabled: disabled(),
              })}
            >
              <Icon color="sand900" name={props.iconName} size="24" />
              <Show when={props.count}>
                {count => (
                  <span class={itemLinkCountClass({ hue: props.hue })}>
                    {count()}
                  </span>
                )}
              </Show>
            </A>
          </Tooltip>
        }
      >
        <A
          href={props.href}
          class={itemLinkClass({
            mobile: true,
            disabled: disabled(),
          })}
        >
          <Icon color="sand800" name={props.iconName} size="18" />
          <span>
            {props.label}
            {disabledReason() && ` (${disabledReason()})`}
          </span>
          <Show when={props.count}>
            {count => (
              <span class={itemLinkCountClass({ hue: props.hue })}>
                {count()}
              </span>
            )}
          </Show>
        </A>
      </Show>
    </li>
  )
}
